import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import routeConfig from '../../routes/routeConfig';
import useAuth from '../../hooks/useAuth';
import usePendingApprovalsCount from '../../hooks/usePendingApprovalsCount';
import { HIDDEN_MENU_PATHS } from '../../config';
import NavIcon from '../ui/NavIcon';
import { SECTIONS, NAV_ITEMS } from '../../config/nav-config';

const LS_KEY = 'sidebarCollapsedSections';

const BADGE_SELECTORS = {
  pendingApprovals: (ctx) => ctx.pendingApprovalsCount,
};

function loadCollapsed() {
  try {
    const raw =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem(LS_KEY)
        : null;
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveCollapsed(set) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(LS_KEY, JSON.stringify([...set]));
  } catch {
    /* quota / private mode — no-op */
  }
}

function SectionHeader({ sectionId, labelText, isCollapsed, badgeCount, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      aria-controls={`section-${sectionId}`}
      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
    >
      <span>{labelText}</span>
      <span className="flex items-center gap-2">
        {isCollapsed && badgeCount > 0 && (
          <span
            data-testid={`section-${sectionId}-badge`}
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800"
          >
            {badgeCount}
          </span>
        )}
        <ChevronRightIcon
          className={`h-4 w-4 transform transition-transform duration-200 motion-reduce:transition-none ${
            isCollapsed ? '' : 'rotate-90'
          }`}
          aria-hidden="true"
        />
      </span>
    </button>
  );
}

function Sidebar({ isOpen, onClose }) {
  const { t } = useTranslation(['menu', 'ui']);
  const location = useLocation();
  const { hasAnyRole } = useAuth();
  // Only admins can read the approvals endpoint (route requiredRoles: [ADMIN]).
  // Gate polling so scoped roles don't fire a guaranteed-403 request.
  const pendingApprovalsCount = usePendingApprovalsCount(hasAnyRole(['admin']));
  const [collapsed, setCollapsedState] = useState(loadCollapsed);

  const ctx = { pendingApprovalsCount };

  const toggleSection = useCallback((id) => {
    setCollapsedState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return next;
    });
  }, []);

  const isActive = (path) =>
    location.pathname === path
      ? 'bg-primary-100 text-primary-900'
      : 'text-secondary-600 hover:bg-gray-50';

  const isAllowed = useCallback(
    (path) => {
      const route = routeConfig.find((r) => r.path === path);
      if (!route) return false;
      if (HIDDEN_MENU_PATHS.includes(path)) return false;
      return route.requiredRoles.length === 0 || hasAnyRole(route.requiredRoles);
    },
    [hasAnyRole],
  );

  const visibleSections = useMemo(
    () =>
      SECTIONS.map((section) => {
        const items = NAV_ITEMS.filter((i) => i.section === section.id && isAllowed(i.path));
        const badgeCount = items.reduce(
          (sum, item) =>
            sum + (item.badge ? (BADGE_SELECTORS[item.badge]?.(ctx) ?? 0) : 0),
          0,
        );
        return { ...section, items, badgeCount };
      }).filter((s) => s.items.length > 0),
    // ctx is built from pendingApprovalsCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAllowed, pendingApprovalsCount],
  );

  // Auto-expand the section containing the active route (one-shot per route change).
  // Only depends on pathname; collapsed state read via setCollapsedState's prev arg.
  useEffect(() => {
    const activeItem = NAV_ITEMS.find((i) => i.path === location.pathname);
    if (!activeItem) return;
    setCollapsedState((prev) => {
      if (!prev.has(activeItem.section)) return prev; // identity unchanged → no re-render
      const next = new Set(prev);
      next.delete(activeItem.section);
      saveCollapsed(next);
      return next;
    });
  }, [location.pathname]);

  const renderNavItem = (item) => {
    const badgeFn = item.badge ? BADGE_SELECTORS[item.badge] : null;
    const badgeValue = badgeFn ? badgeFn(ctx) : null;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={isOpen ? onClose : undefined}
        className={`${isActive(item.path)} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
      >
        <NavIcon name={item.iconName} className="mr-3 h-5 w-5" />
        <span className="flex-1">{t(item.labelKey)}</span>
        {badgeValue > 0 && (
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            {badgeValue}
          </span>
        )}
      </Link>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
      <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex-1 px-3 space-y-3">
          {visibleSections.map((section) => {
            const isCollapsed = collapsed.has(section.id);
            return (
              <div key={section.id} className="space-y-1">
                <SectionHeader
                  sectionId={section.id}
                  labelText={t(section.labelKey)}
                  isCollapsed={isCollapsed}
                  badgeCount={section.badgeCount}
                  onToggle={() => toggleSection(section.id)}
                />
                {!isCollapsed && (
                  <div id={`section-${section.id}`} className="space-y-1">
                    {section.items.map(renderNavItem)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isOpen && (
        <div className="md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
            onClick={onClose}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-white">
            <div className="flex justify-end p-3 border-b border-gray-200 bg-white">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close sidebar"
                className="text-secondary-600 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 6l12 12M6 18L18 6"
                  />
                </svg>
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">{sidebarContent}</div>
      </div>
    </>
  );
}

export default Sidebar;
